import React, {ReactNode, useContext, useEffect, useRef, useState} from 'react';

import ReactHtmlParser from 'react-html-parser';
import {ButtonDropdown, DropdownMenu, DropdownToggle} from 'reactstrap';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ContentEditable} from 'app/components/general/Mentionable';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import StateButton from 'app/general/widgets/StateButton';
import {useSendMessageWithHistory} from 'app/openai/hooks';
import {gt} from 'app/utils/i18n';
import {isEmptyString} from 'app/utils/strings';
import {WebsocketContext} from 'app/ws/context';

import {
  ChatMessage,
  type ChatMessageType,
  TypingIndicator,
} from './ChatMessage';

import type {AIPromptExampleType} from 'app/openai/types';
import type {ChatTypes} from 'app/subscriptions/hooks';

interface ChatMessageProps {
  chatMessage: string;
  setChatMessage: (message: string) => void;
}

interface ChatPanelProps {
  messageHistory: Array<ChatMessageType>;
  chatStatus: 'ready' | 'sending' | 'processing' | 'error' | 'streaming';
}

export function ChatPanel({messageHistory, chatStatus}: ChatPanelProps) {
  return (
    <>
      {chatStatus === 'processing' || chatStatus === 'sending' ? (
        <ChatMessage message={{source: 'ai', text: <TypingIndicator />}} />
      ) : null}
      {messageHistory.map((message, iteration) => (
        <ChatMessage
          key={`chat-message-${iteration}`}
          message={{...message, text: ReactHtmlParser(message.text)}}
        />
      ))}
    </>
  );
}

interface InputChatProps extends ChatMessageProps, ChatPanelProps {
  submitMessage: () => void;
  disabled: boolean;
  placeholder: string;
}

export function InputChat({
  chatStatus,
  chatMessage,
  setChatMessage,
  messageHistory,
  placeholder,
  disabled,
  submitMessage,
}: InputChatProps) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (
      ref.current &&
      messageHistory.length > 1 &&
      chatStatus !== 'processing' &&
      chatStatus !== 'sending' &&
      chatStatus !== 'streaming'
    ) {
      ref.current.focus();
    }
  }, [messageHistory, chatStatus]);

  const onSubmit = (e) => {
    e.preventDefault();
    submitMessage();
  };

  return (
    <div className="input-group">
      <ContentEditable
        className="form-control overflow-auto h-auto"
        disabled={
          chatStatus === 'processing' ||
          chatStatus === 'sending' ||
          chatStatus === 'streaming' ||
          disabled
        }
        onSubmit={onSubmit}
        value={chatMessage}
        placeholder={placeholder}
        style={{
          maxHeight: '8em',
        }}
        onChange={setChatMessage}
        ref={ref}
      />
      <div className="input-group-append">
        <StateButton
          className="btn rounded-0 btn-primary shadow-none"
          busyContent={<i className="far fa-exclamation-circle"></i>}
          disabled={
            chatStatus === 'processing' ||
            chatStatus === 'sending' ||
            chatStatus === 'streaming' ||
            isEmptyString(chatMessage) ||
            disabled
          }
          isIdle={true}
          onClick={onSubmit}
        >
          <i className="fa fa-paper-plane"></i>
        </StateButton>
      </div>
    </div>
  );
}

interface ModelTitleProps {
  setChatMessage: ChatMessageProps['setChatMessage'];
  toggleMessagePanel?: () => void;
  prompts?: Array<AIPromptExampleType>;
  title?: string;
}

function ModelTitle({
  setChatMessage,
  toggleMessagePanel,
  prompts,
  title,
}: ModelTitleProps) {
  return (
    <ViewModeTitle
      title={title}
      actionsBox={
        <>
          <AIPrompts
            prompts={prompts}
            onClick={(prompt) => setChatMessage(prompt.prompt)}
          />
          {toggleMessagePanel ? (
            <div className="btn-group title-buttons d-print-none">
              <LazyTooltip tooltip={gt('Hide SweetAI')}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleMessagePanel}
                >
                  <i className="fas fa-fw fa-chevron-right" />
                </button>
              </LazyTooltip>
            </div>
          ) : null}
        </>
      }
    />
  );
}

interface MessagePanelProps extends InputChatProps, ModelTitleProps {
  className?: string;
}
export const MessagePanel = ({
  className,
  messageHistory,
  chatMessage,
  chatStatus,
  submitMessage,
  setChatMessage,
  toggleMessagePanel,
  prompts,
  title,
  disabled,
  placeholder,
}: MessagePanelProps) => {
  return (
    <BaseTemplateForChatMessages
      className={className}
      modelTitle={
        <ModelTitle
          setChatMessage={setChatMessage}
          title={title}
          toggleMessagePanel={toggleMessagePanel}
          prompts={prompts}
        />
      }
      chatPanel={
        <ChatPanel chatStatus={chatStatus} messageHistory={messageHistory} />
      }
      inputPanel={
        <InputChat
          submitMessage={submitMessage}
          disabled={disabled}
          placeholder={placeholder}
          chatMessage={chatMessage}
          chatStatus={chatStatus}
          setChatMessage={setChatMessage}
          messageHistory={messageHistory}
        />
      }
    />
  );
};

interface BaseTemplateForChatMessagesProps {
  className?: string;
  modelTitle: ReactNode;
  chatPanel: ReactNode;
  inputPanel: ReactNode;
}

function BaseTemplateForChatMessages({
  className,
  modelTitle,
  chatPanel,
  inputPanel,
}: BaseTemplateForChatMessagesProps) {
  return (
    <div
      className={className != null ? className : 'col-md-6'}
      style={{zIndex: 0}}
    >
      {modelTitle}
      <div className="d-flex flex-column vh-100 pre-scrollable">
        <div className="flex-grow-1 overflow-auto d-flex flex-column-reverse px-1">
          {chatPanel}
        </div>
      </div>
      {inputPanel}
    </div>
  );
}

interface AIPromptsProps {
  prompts?: Array<AIPromptExampleType>;
  onClick: (prompt: AIPromptExampleType) => unknown;
}

export function AIPrompts({prompts, onClick}: AIPromptsProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (prompts == null || prompts.length === 0) {
    return null;
  }
  return (
    <ButtonDropdown isOpen={isOpen} toggle={() => setIsOpen((open) => !open)}>
      <DropdownToggle
        caret
        size="sm"
        color="prim"
        className="btn-text border-right pr-2"
      >
        {gt('Prompts')}
      </DropdownToggle>
      <DropdownMenu>
        <StyledDropdownMenu>
          {prompts.map((prompt) => (
            <a
              key={prompt.name}
              className="list-group-item list-group-item-action text-truncate"
              href="#"
              onClick={() => {
                onClick(prompt);
                setIsOpen(false);
              }}
            >
              <h5 className="mb-1">{prompt.name}</h5>
              <small className="mb-1 text-muted">{prompt.prompt}</small>
            </a>
          ))}
        </StyledDropdownMenu>
      </DropdownMenu>
    </ButtonDropdown>
  );
}

const StyledDropdownMenu = styled.div`
  width: 500px;
  h5 {
    text-transform: none;
    font-weight: normal;
  }
`;

type MessageExchangeChatPanelProps = {
  threadId: string;
  chatType: ChatTypes;
  object_id?: number;
  content_type?: string;
  disabled?: boolean;
  initialAIMessage?: string;
  placeholder: string;
};

interface MessageExchangeProps extends MessageExchangeChatPanelProps {
  placeholderMessage?: string;
  toggleMessagePanel?: () => void;
  prompts?: Array<AIPromptExampleType>;
}
export const MessageExchange = ({
  threadId,
  chatType,
  object_id,
  content_type,
  toggleMessagePanel,
  prompts,
  disabled = false,
  initialAIMessage,
  placeholderMessage = '',
  placeholder,
}: MessageExchangeProps) => {
  const [chatMessage, setChatMessage] = useState<string>(placeholderMessage);

  const {chatStatus, messageHistory, submitMessage} = useSendMessageWithHistory(
    {
      type: chatType,
      object_id,
      content_type,
      thread_id: threadId,
    },
    chatMessage,
    setChatMessage,
    initialAIMessage
  );

  const {isWSOpen} = useContext(WebsocketContext);

  return (
    <BaseTemplateForChatMessages
      className="mt-0 mb-4"
      modelTitle={
        <ModelTitle
          setChatMessage={setChatMessage}
          title={gt('Sweet AI')}
          toggleMessagePanel={toggleMessagePanel}
          prompts={prompts}
        />
      }
      chatPanel={
        <ChatPanel messageHistory={messageHistory} chatStatus={chatStatus} />
      }
      inputPanel={
        <InputChat
          chatMessage={chatMessage}
          setChatMessage={setChatMessage}
          placeholder={placeholder}
          disabled={!isWSOpen || disabled}
          messageHistory={messageHistory}
          chatStatus={chatStatus}
          submitMessage={submitMessage}
        />
      }
    />
  );
};
